import React from 'react';
import { useForm } from 'react-hook-form';
import { Pencil, Send, Rocket, Loader2 } from 'lucide-react';
import { useMutation, useQuery, useAction } from 'convex/react';
import { useUser } from '@clerk/clerk-react';
import { useContext } from 'react';
import { BlogWriterContext } from '../../contexts/BlogWriterContext';
import { api } from "../../lib/convex";
import WritingStylesManager from '../WritingStylesManager';
import { SERVICE_TIERS, isAnthropicModel } from "../../../convex/constants/serviceTiers";
import { getTierFeatures } from "../../../convex/constants/serviceTiers";
import StandardOptions from './StandardOptions';
import MarketingOptions from './MarketingOptions';
import BlogContent from './BlogContent';
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { Toaster } from "@/components/ui/toaster";
import { Footerdemo } from '../ui/footer-section';
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

type BlogFormData = {
  contentType: string;
  title: string;
  keywords: string;
  context?: string;
  model: string;
  industry: string;
  tone: string;
  wordCount: number;
  framework: string;
  readabilityLevel: string;
  writingStyleId: string;
  enableSectionLabeling: boolean;
  includeIntroduction: boolean;
  longerParagraphs: boolean;
  includeConclusion: boolean;
  includeKeyTakeaways: boolean;
  enableExtendedThinking: boolean;
  targetAudience: string;
  contentGoal: string;
  ctaType: string;
  competitorAngle: string;
};

type WritingStyleState = 'select' | 'custom';

export default function BlogWriter() {
  const [selectedPostId, setSelectedPostId] = React.useState<string | null>(null);

  const { isStylesOpen, setIsStylesOpen } = useContext(BlogWriterContext);

  const { toast } = useToast();
  const generatePost = useAction(api.posts.generate);
  const initChain = useAction(api.posts.initChain);
  const finalChain = useAction(api.posts.finalChain);
  const createStyle = useMutation(api.posts.createWritingStyle);
  const selectedPost = useQuery(api.posts.getPostById, 
    selectedPostId ? { postId: selectedPostId } : "skip"
  );
  const preferences = useQuery(api.posts.getUserPreferences);
  const writingStyles = useQuery(api.posts.getWritingStyles);
  const { user } = useUser();
  const [writingStyleState, setWritingStyleState] = React.useState<WritingStyleState>('select');
  const [selectedModel, setSelectedModel] = React.useState<string>(() => {
    const tier = (preferences?.serviceTier || user?.privateMetadata?.serviceTier || 'free') as keyof typeof SERVICE_TIERS;
    return SERVICE_TIERS[tier].model;
  });
  const [customStylePrompt, setCustomStylePrompt] = React.useState('');
  const [isMarketerMode, setIsMarketerMode] = React.useState(false);
  const [isGenerating, setIsGenerating] = React.useState(false);
  const [generatedContent, setGeneratedContent] = React.useState<{ content: string; seoScore: number } | null>(null);
  const [enableExtendedThinking, setEnableExtendedThinking] = React.useState(false);
  
  // State for two-stage generation
  const [partialConversation, setPartialConversation] = React.useState<any[]>([]);
  const [isFirstStageComplete, setIsFirstStageComplete] = React.useState(false);

  // Get user's service tier and features
  const tier = React.useMemo(() => 
    (preferences?.serviceTier || user?.privateMetadata?.serviceTier || 'free') as keyof typeof SERVICE_TIERS,
    [preferences?.serviceTier, user?.privateMetadata?.serviceTier]
  );
  
  const { tierFeatures, availableModels } = React.useMemo(() => {
    const features = getTierFeatures(tier);
    return {
      tierFeatures: features,
      availableModels: features.availableModels
    };
  }, [tier]);

  const defaultValues = React.useMemo(() => ({
    enableSectionLabeling: true,
    wordCount: 1000,
    contentType: 'guide',
    model: tierFeatures.model,
    readabilityLevel: 'grade8',
    tone: 'professional',
    includeIntroduction: Boolean(true),
    longerParagraphs: Boolean(false),
    includeConclusion: Boolean(true),
    includeKeyTakeaways: Boolean(true),
    framework: 'none',
    targetAudience: 'b2b',
    contentGoal: 'awareness',
    ctaType: 'soft',
    competitorAngle: 'direct',
    enableExtendedThinking: Boolean(false),
  }), [tierFeatures]);

  // Update selectedModel when preferences change
  React.useEffect(() => {
    if (preferences?.selectedModel && availableModels.includes(preferences.selectedModel)) {
      setSelectedModel(preferences.selectedModel);
    } else if (!availableModels.includes(selectedModel)) {
      setSelectedModel(tierFeatures.model);
    }
  }, [preferences?.selectedModel, availableModels, selectedModel, tierFeatures.model]);

  const defaultPreferences = React.useMemo(() => ({
    isMarketerMode: preferences?.isMarketerMode || false,
  }), [preferences?.isMarketerMode]);
  
  const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm<BlogFormData>({ 
    defaultValues,
  });

  // Watch the enableExtendedThinking value
  const watchExtendedThinking = watch('enableExtendedThinking');

  React.useEffect(() => {
    setIsMarketerMode(defaultPreferences.isMarketerMode);
  }, [defaultPreferences.isMarketerMode]);

  // Function to handle first stage of generation
  const startFirstStage = async (model: string) => {
    try {
      setIsGenerating(true);
      setIsFirstStageComplete(false);
      
      const result = await initChain({
        model,
      });
      
      setPartialConversation(result.partialConversation);
      setIsFirstStageComplete(true);
      
      return true;
    } catch (error) {
      console.error('Error in first stage generation:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to initialize generation",
        variant: "destructive"
      });
      setIsGenerating(false);
      return false;
    }
  };

  // Function to handle second stage of generation
  const startSecondStage = async (data: BlogFormData) => {
    try {
      // Handle custom writing style if needed
      let styleId = data.writingStyleId;
      if (writingStyleState === 'custom' && customStylePrompt) {
        styleId = await createStyle({
          name: "Custom Style",
          description: "Custom writing style",
          systemPrompt: customStylePrompt,
          isCustom: true,
        });
      }

      // Prepare keywords array from comma-separated string
      const keywords = data.keywords.split(',').map(k => k.trim());
      
      // Call the finalChain function with the partial conversation
      const result = await finalChain({
        partialConversation,
        title: data.title,
        keywords,
        model: selectedModel,
        readabilityLevel: data.readabilityLevel,
        industry: data.industry,
        tone: data.tone,
        wordCount: data.wordCount,
        framework: data.framework === 'none' ? undefined : data.framework,
        writingStyleId: styleId,
        enableSectionLabeling: data.enableSectionLabeling === true,
        includeIntroduction: data.includeIntroduction === true,
        longerParagraphs: data.longerParagraphs === true,
        includeConclusion: data.includeConclusion === true,
        includeKeyTakeaways: data.includeKeyTakeaways === true,
        enableExtendedThinking: data.enableExtendedThinking === true,
      });
      
      setGeneratedContent(result);
    } catch (error) {
      console.error('Error in second stage generation:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to generate post",
        variant: "destructive"
      });
    } finally {
      setIsGenerating(false);
      setIsFirstStageComplete(false);
    }
  };

  const onSubmit = React.useCallback(async (data: BlogFormData) => {
    try {
      setIsGenerating(true);
      setGeneratedContent(null);
      
      // Use two-stage approach for all models
      const usesTwoStage = true;
      
      if (usesTwoStage) {
        // Start first stage
        const firstStageSuccess = await startFirstStage(selectedModel);
        
        if (firstStageSuccess) {
          // If first stage was successful, start second stage
          await startSecondStage(data);
        }
      } else {
        // Use the original generation approach as fallback
        if (writingStyleState === 'custom' && customStylePrompt) {
          const styleId = await createStyle({
            name: "Custom Style",
            description: "Custom writing style",
            systemPrompt: customStylePrompt,
            isCustom: true,
          });
          data.writingStyleId = styleId;
        }

        // Ensure boolean values are properly set
        const formData = {
          ...data,
          model: selectedModel,
          context: data.context || '',
          keywords: data.keywords.split(',').map(k => k.trim()),
          includeIntroduction: data.includeIntroduction === true,
          longerParagraphs: data.longerParagraphs === true,
          includeConclusion: data.includeConclusion === true,
          includeKeyTakeaways: data.includeKeyTakeaways === true,
          enableSectionLabeling: data.enableSectionLabeling === true,
          enableExtendedThinking: data.enableExtendedThinking === true,
          framework: data.framework === 'none' ? undefined : data.framework,
        };

        const result = await generatePost({
          ...formData,
        });
        setGeneratedContent(result);
        setIsGenerating(false);
      }
    } catch (error) {
      console.error('Error generating post:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to generate post",
        variant: "destructive"
      });
      setIsGenerating(false);
    }
  }, [writingStyleState, customStylePrompt, createStyle, generatePost, selectedModel, toast, initChain, finalChain]);

  const handleWritingStyleChange = React.useCallback((value: string) => {
    if (value === 'custom') {
      setWritingStyleState('custom');
    } else {
      setWritingStyleState('select');
      register('writingStyleId').onChange({ target: { value } });
    }
  }, [register]);

  // Check if the selected model supports extended thinking
  const supportsExtendedThinking = React.useMemo(() => {
    // Only Claude 3.7 Sonnet and Claude 4 models support extended thinking
    return selectedModel === 'claude-3-7-sonnet-latest' || 
           selectedModel === 'claude-4-sonnet-latest' || 
           selectedModel === 'claude-4-opus-latest';
  }, [selectedModel]);

  return (
    <div className="min-h-screen flex flex-col bg-cream">
      <div className="h-16" /> {/* Spacer for fixed header */}
      <div className="relative flex-1">
        <WritingStylesManager 
          isOpen={isStylesOpen}
        />
        <div className={`w-full px-4 lg:px-8 py-6 transition-all duration-300 ${
          isStylesOpen ? 'mr-96' : 'mr-0'
        }`}>
          {selectedPost ? (
            <div className="max-w-4xl mx-auto">
              <BlogContent
                content={selectedPost.content}
                title={selectedPost.title}
                onReset={() => setSelectedPostId(null)}
              />
              <Toaster />
            </div>
          ) : (
            <div className="w-full max-w-none">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-4">
                  <h1 className="text-xl font-bold text-gray-900">AI Blog Writer (That Doesn't Suck)</h1>
                </div>
                <div className="text-sm text-gray-600">
                  Welcome, {user?.firstName || 'User'}
                </div>
              </div>

              {!isGenerating && !generatedContent && (
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                  <StandardOptions
                    register={register}
                    errors={errors}
                    setValue={setValue}
                    handleWritingStyleChange={handleWritingStyleChange}
                    writingStyleState={writingStyleState}
                    customStylePrompt={customStylePrompt}
                    setCustomStylePrompt={setCustomStylePrompt}
                    selectedModel={selectedModel}
                    setSelectedModel={setSelectedModel}
                    availableModels={availableModels}
                    tier={tier}
                    tierFeatures={tierFeatures}
                    writingStyles={writingStyles || []}
                  />

                  {/* Extended Thinking Toggle - Only show for supported models */}
                  {supportsExtendedThinking && (
                    <div className="pt-4 border-t">
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="enableExtendedThinking"
                          checked={watchExtendedThinking}
                          onCheckedChange={(checked) => setValue('enableExtendedThinking', checked)}
                        />
                        <Label htmlFor="enableExtendedThinking" className="text-sm">
                          Enable Extended Thinking
                        </Label>
                      </div>
                      <p className="text-xs text-gray-500 mt-1 ml-7">
                        Allows the AI to spend more time thinking about the content before generating it. 
                        This can improve quality but may take longer. Only available for Claude 3.7 Sonnet and Claude 4 models.
                      </p>
                    </div>
                  )}

                  {/* Marketer Mode Toggle */}
                  <div className="pt-6 border-t">
                    <button
                      onClick={() => setIsMarketerMode(!isMarketerMode)}
                      className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-all duration-300 ${
                        isMarketerMode 
                          ? 'bg-dogbrown text-white' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                      type="button"
                    >
                      <Rocket className={`w-4 h-4 transition-transform duration-300 ${
                        isMarketerMode ? 'rotate-45' : ''
                      }`} />
                      <span className="font-medium">
                        {isMarketerMode ? 'Hide Marketing Options' : 'Show Marketing Options'}
                      </span>
                    </button>
                  </div>

                  {/* Marketing Options */}
                  <div className={`transition-all duration-300 overflow-hidden ${
                    isMarketerMode ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'
                  }`}>
                    <MarketingOptions
                      register={register}
                      isMarketerMode={isMarketerMode}
                    />
                  </div>

                  {/* Generate Button - Positioned ABOVE the separator */}
                  <div className="mt-8">
                    <Button className="w-full bg-black hover:bg-black/80 text-white" type="submit">
                      <Send className="w-4 h-4 mr-2" />
                      Generate Blog Post
                    </Button>
                  </div>
                  
                  {/* Separator line */}
                 <div className="pt-2 border-t"></div>
                </form>
              )}

              {isGenerating && (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="relative w-36 h-36 flex items-center justify-center">
                    <Loader2 className="w-36 h-36 text-dogbrown animate-pulse" />
                    <Loader2 className="w-8 h-8 text-white absolute animate-spin" />
                  </div>
                  <p className="mt-4 text-lg text-gray-700">
                    {isFirstStageComplete 
                      ? "Generating your blog post... This may take a minute or two."
                      : "Initializing the AI model..."}
                  </p>
                  {isFirstStageComplete && (
                    <p className="mt-2 text-sm text-gray-500">
                      Creating a high-quality article with your specifications...
                    </p>
                  )}
                  {watchExtendedThinking && supportsExtendedThinking && (
                    <p className="mt-2 text-sm text-blue-600">
                      Extended thinking is enabled. This may take a bit longer but will produce better results.
                    </p>
                  )}
                </div>
              )}

              {generatedContent && (
                <BlogContent
                  content={generatedContent.content}
                  title={selectedPost?.title}
                  onReset={() => {
                    setGeneratedContent(null);
                    setIsGenerating(false);
                  }}
                />
              )}
              <Toaster />
            </div>
          )}
        </div>
      </div>
      <Footerdemo />
    </div>
  );
}